import React, { useState } from 'react';
import { Shield, Search, Target, TrendingUp, Calendar, ChevronRight, CheckCircle2, XCircle, ArrowRight, Layers, Monitor, Smartphone, Watch, Laptop, Tablet, ShoppingCart, BarChart3, Users, Megaphone, Eye, MousePointerClick, DollarSign } from 'lucide-react';

const tabs = [
  { id: 'trust', label: 'Güven Hiyerarşisi', icon: Shield },
  { id: 'iphone', label: 'iPhone', icon: Smartphone },
  { id: 'macbook', label: 'MacBook', icon: Laptop },
  { id: 'ipad', label: 'iPad', icon: Tablet },
  { id: 'watch', label: 'Apple Watch', icon: Watch },
  { id: 'google', label: 'Google Ads', icon: Search },
  { id: 'meta', label: 'Meta Ads', icon: Megaphone },
  { id: 'funnel', label: 'Funnel & CRO', icon: TrendingUp },
  { id: 'actions', label: '30 Gün Aksiyon', icon: Calendar },
];

const trustItems = [
  { rank: 1, label: 'Apple Yetkili Yenileme Merkezi', note: 'H1\'e pin\'lenecek — her zaman ilk başlık' },
  { rank: 2, label: '12 Ay TSE Sertifikalı Garanti', note: 'H2 veya D1\'de mutlaka yer alacak' },
  { rank: 3, label: '21 Gün İçinde Koşulsuz İade', note: 'Callout extension + kreatif rozet' },
  { rank: 4, label: '90 Gün İçinde Değişim Hakkı', note: 'Callout extension + kreatif rozet' },
  { rank: 5, label: 'Gürgençler Güvencesi (15 Yıl)', note: 'Ticaret Bakanlığı Lisanslı • Bilişim Vadisi Teknopark' },
];

const compareRows = [
  ['Fiyat/taksit odaklı mesaj', 'Güven/uzmanlık/yetki odaklı mesaj'],
  ['"3.549₺\'den başlayan fiyatlarla"', '"Apple Yetkili Yenileme Merkezi\'nde"'],
  ['"12 taksit fırsatı"', '"12 Ay TSE Garantili • 27 Nokta Kontrol"'],
  ['Tüm ürünler tek mesaj', 'Ürün grubuna özel mesaj mimarisi'],
  ['Güven rozetleri küçük dipnot', 'Güven rozetleri ana görsel element'],
  ['Sadece iPhone kreatifleri', 'Mac, iPad, Watch ayrı kreatifler'],
];

function Badge({ color = 'blue', children }) {
  const colors = {
    blue: 'bg-blue-100 text-blue-700',
    green: 'bg-green-100 text-green-700',
    red: 'bg-red-100 text-red-700',
    yellow: 'bg-yellow-100 text-yellow-700',
    purple: 'bg-purple-100 text-purple-700',
    orange: 'bg-orange-100 text-orange-700',
    gray: 'bg-gray-100 text-gray-600',
  };
  return <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium ${colors[color]}`}>{children}</span>;
}

function Card({ title, subtitle, icon: Icon, accent = 'blue', children }) {
  const accents = {
    blue: 'border-l-blue-500', green: 'border-l-green-500', red: 'border-l-red-500',
    yellow: 'border-l-yellow-500', purple: 'border-l-purple-500', orange: 'border-l-orange-500',
  };
  return (
    <div className={`bg-white rounded-xl shadow-sm border border-gray-200 border-l-4 ${accents[accent]} p-6`}>
      {(title || Icon) && (
        <div className="flex items-center gap-3 mb-4">
          {Icon && <Icon className="w-5 h-5 text-gray-500" />}
          <div>
            {title && <h3 className="font-semibold text-gray-900">{title}</h3>}
            {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
          </div>
        </div>
      )}
      {children}
    </div>
  );
}

function AdPreview({ url, title, desc, extensions }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 my-3">
      <div className="text-xs text-gray-500 mb-1">{url}</div>
      <div className="text-blue-700 font-normal text-sm mb-1 leading-snug">{title}</div>
      <div className="text-gray-600 text-xs leading-relaxed">{desc}</div>
      {extensions && (
        <div className="flex flex-wrap gap-2 mt-2">
          {extensions.map((ext, i) => (
            <span key={i} className="text-xs text-blue-600">{ext}{i < extensions.length - 1 ? ' ·' : ''}</span>
          ))}
        </div>
      )}
    </div>
  );
}

function KeywordTable({ keywords }) {
  const intentColors = { Yüksek: 'green', Brand: 'blue', Orta: 'yellow', Araştırma: 'orange' };
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-50">
            <th className="text-left px-3 py-2 font-semibold text-gray-600 text-xs uppercase tracking-wide">Anahtar Kelime</th>
            <th className="text-left px-3 py-2 font-semibold text-gray-600 text-xs uppercase tracking-wide">Niyet</th>
            <th className="text-left px-3 py-2 font-semibold text-gray-600 text-xs uppercase tracking-wide">Eşleme</th>
            {keywords[0]?.campaign && <th className="text-left px-3 py-2 font-semibold text-gray-600 text-xs uppercase tracking-wide">Kampanya</th>}
            {keywords[0]?.note && <th className="text-left px-3 py-2 font-semibold text-gray-600 text-xs uppercase tracking-wide">Not</th>}
          </tr>
        </thead>
        <tbody>
          {keywords.map((kw, i) => (
            <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
              <td className="px-3 py-2 font-mono text-xs">{kw.keyword}</td>
              <td className="px-3 py-2"><Badge color={intentColors[kw.intent]}>{kw.intent}</Badge></td>
              <td className="px-3 py-2 text-gray-600 text-xs">{kw.match}</td>
              {kw.campaign && <td className="px-3 py-2 text-gray-500 text-xs">{kw.campaign}</td>}
              {kw.note && <td className="px-3 py-2 text-gray-500 text-xs">{kw.note}</td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── SECTIONS ────────────────────────────────────────────────

function TrustSection() {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card title="Sabit Güven Hiyerarşisi (Pin Sırası)" subtitle="Her reklamda bu sıra korunacak" icon={Shield} accent="blue">
        <div className="space-y-3">
          {trustItems.map(item => (
            <div key={item.rank} className="flex items-center gap-3 bg-blue-50 rounded-lg p-3 border-l-3 border-blue-400">
              <span className="w-7 h-7 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-sm shrink-0">{item.rank}</span>
              <div>
                <div className="font-medium text-gray-900 text-sm">{item.label}</div>
                <div className="text-xs text-gray-500">{item.note}</div>
              </div>
            </div>
          ))}
        </div>
      </Card>

      <div className="space-y-6">
        <Card title="Mevcut Durum → Hedef" subtitle="Reklam dili değişimi" icon={ArrowRight} accent="red">
          <div className="space-y-2">
            {compareRows.map(([old, next], i) => (
              <div key={i} className="grid grid-cols-[1fr_auto_1fr] gap-3 items-center py-2 border-b border-gray-100 last:border-0">
                <span className="text-xs text-red-500 line-through opacity-70">{old}</span>
                <ArrowRight className="w-3 h-3 text-gray-400" />
                <span className="text-xs text-green-600 font-medium">{next}</span>
              </div>
            ))}
          </div>
        </Card>

        <Card title="Kreatif Güven Rozet Standardı" subtitle="Her görselde yer alacak sabit elementler" icon={CheckCircle2} accent="green">
          <p className="text-xs text-gray-500 mb-3">Her Yenilio kreatifinde aşağıdaki rozetler görünür ve standart boyutta olacak:</p>
          <div className="flex flex-wrap gap-2">
            <Badge color="blue">🛡️ Apple Yetkili Yenileme</Badge>
            <Badge color="green">✅ 12 Ay TSE Garanti</Badge>
            <Badge color="yellow">↩️ 21 Gün İade</Badge>
            <Badge color="purple">🔄 90 Gün Değişim</Badge>
            <Badge color="orange">🏛️ Ticaret Bakanlığı Onaylı</Badge>
          </div>
        </Card>
      </div>
    </div>
  );
}

function IPhoneSection() {
  const keywords = [
    { keyword: 'yenilio', intent: 'Brand', match: 'Exact', campaign: 'Brand' },
    { keyword: 'yenilio iphone', intent: 'Brand', match: 'Exact', campaign: 'Brand' },
    { keyword: 'yenilenmiş iphone 15', intent: 'Yüksek', match: 'Exact', campaign: 'iPhone_15' },
    { keyword: 'yenilenmiş iphone 14 pro', intent: 'Yüksek', match: 'Exact', campaign: 'iPhone_14' },
    { keyword: 'ikinci el iphone garantili', intent: 'Yüksek', match: 'Phrase', campaign: 'iPhone_Genel' },
    { keyword: 'apple yetkili yenilenmiş iphone', intent: 'Yüksek', match: 'Phrase', campaign: 'iPhone_Genel' },
    { keyword: 'TSE garantili iphone', intent: 'Yüksek', match: 'Phrase', campaign: 'iPhone_Genel' },
    { keyword: '2. el iphone 15', intent: 'Orta', match: 'Phrase', campaign: 'iPhone_15' },
    { keyword: 'ucuz iphone 14', intent: 'Orta', match: 'Phrase', campaign: 'iPhone_14' },
    { keyword: 'ikinci el iphone nereden alınır', intent: 'Araştırma', match: 'Broad', campaign: 'iPhone_Genel' },
    { keyword: 'yenilenmiş telefon güvenilir mi', intent: 'Araştırma', match: 'Broad', campaign: 'iPhone_Genel' },
  ];

  const headlines = [
    { pos: 'H1', text: 'Apple Yetkili Yenileme: iPhone 15', pin: '📌 PIN 1' },
    { pos: 'H2', text: '12 Ay TSE Garantili Yenilio Yenisi', pin: '📌 PIN 2' },
    { pos: 'H3', text: '27 Nokta Kontrollü iPhone', pin: null },
    { pos: 'H4', text: '21 Gün Koşulsuz İade Hakkı', pin: null },
    { pos: 'H5', text: 'Gürgençler Güvencesiyle iPhone 15', pin: null },
    { pos: 'H6', text: 'Ticaret Bakanlığı Lisanslı', pin: null },
    { pos: 'H7', text: 'Peşin Fiyatına 12 Ay Taksit', pin: '📌 PIN 3' },
    { pos: 'H8', text: '90 Gün İçinde Değişim Hakkı', pin: null },
    { pos: 'H9', text: "Yenilio'da iPhone 15 Keşfet", pin: null },
    { pos: 'H10', text: 'Akıllı Seçim: Yenilenmiş iPhone', pin: null },
  ];

  return (
    <div>
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
        <p className="text-sm"><strong className="text-blue-700">Temel Mesaj:</strong> "Yenilenmiş deme, Yenilio yenisi de. Apple Yetkili Yenileme Merkezi'nde 12 ay TSE garantili iPhone. Gürgençler güvencesiyle."</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card title="Google Ads — Anahtar Kelime Planı" subtitle="Niyet seviyesine göre gruplandırma" icon={Search} accent="blue">
          <KeywordTable keywords={keywords} />
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mt-4">
            <p className="text-xs"><strong className="text-orange-600">Negatifler:</strong> tamiri, arıza, kılıf, ekran koruyucu, sahibinden, letgo</p>
          </div>
        </Card>

        <div className="space-y-6">
          <Card title="Reklam Metni Şablonu (RSA)" subtitle="iPhone 15 örneği — güven odaklı" icon={Monitor} accent="green">
            <AdPreview
              url="yenilio.com/iphone/iphone-15"
              title="Apple Yetkili Yenileme: iPhone 15 — 12 Ay TSE Garantili Yenilio Yenisi"
              desc="27 nokta kontrol, 21 gün koşulsuz iade, 90 gün değişim hakkı. Ticaret Bakanlığı lisanslı Yenilio'da iPhone 15 şimdi peşin fiyatına 12 ay taksitle."
              extensions={['12 Ay TSE Garantisi', '21 Gün İade Hakkı', 'Vade Farksız 12 Taksit', '90 Gün Değişim']}
            />
          </Card>

          <Card title="Headline Pin Stratejisi" subtitle="10 headline'dan 3'ü pin'li" icon={Target} accent="purple">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="bg-gray-50"><th className="text-left px-3 py-2 text-xs font-semibold text-gray-600">Poz.</th><th className="text-left px-3 py-2 text-xs font-semibold text-gray-600">Headline</th><th className="text-left px-3 py-2 text-xs font-semibold text-gray-600">Pin</th></tr></thead>
                <tbody>
                  {headlines.map((h, i) => (
                    <tr key={i} className="border-b border-gray-100"><td className="px-3 py-1.5 text-xs font-mono text-gray-500">{h.pos}</td><td className="px-3 py-1.5 text-xs">{h.text}</td><td className="px-3 py-1.5">{h.pin && <Badge color="red">{h.pin}</Badge>}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          <Card title="Hedef Kitle — Meta" icon={Users} accent="yellow">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="bg-gray-50"><th className="text-left px-3 py-2 text-xs font-semibold text-gray-600">Katman</th><th className="text-left px-3 py-2 text-xs font-semibold text-gray-600">Kitle</th><th className="text-left px-3 py-2 text-xs font-semibold text-gray-600">Yaş</th></tr></thead>
                <tbody>
                  <tr className="border-b border-gray-100"><td className="px-3 py-2"><Badge color="blue">Prospecting</Badge></td><td className="px-3 py-2 text-xs">Apple Inc + iPhone + iOS + Engaged Shoppers</td><td className="px-3 py-2 text-xs">18-45</td></tr>
                  <tr className="border-b border-gray-100"><td className="px-3 py-2"><Badge color="yellow">Consideration</Badge></td><td className="px-3 py-2 text-xs">Site ziyaretçileri (30 gün) + iPhone sayfası görüntüleyenler</td><td className="px-3 py-2 text-xs">18-55</td></tr>
                  <tr><td className="px-3 py-2"><Badge color="green">Conversion</Badge></td><td className="px-3 py-2 text-xs">ATC + Checkout terk + 90 gün alıcılar (cross-sell)</td><td className="px-3 py-2 text-xs">18-55</td></tr>
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}

function MacBookSection() {
  const keywords = [
    { keyword: 'yenilenmiş macbook pro', intent: 'Yüksek', match: 'Exact', note: 'Ana hedef' },
    { keyword: 'yenilenmiş macbook air', intent: 'Yüksek', match: 'Exact', note: 'Ana hedef' },
    { keyword: '2. el macbook garantili', intent: 'Yüksek', match: 'Phrase', note: 'Güven arayan' },
    { keyword: 'apple yetkili yenilenmiş mac', intent: 'Yüksek', match: 'Phrase', note: 'Yeni eklenmeli' },
    { keyword: 'TSE garantili macbook', intent: 'Yüksek', match: 'Phrase', note: 'Yeni eklenmeli' },
    { keyword: 'yenilenmiş macbook m1', intent: 'Yüksek', match: 'Exact', note: 'Model bazlı' },
    { keyword: 'yenilenmiş macbook m2', intent: 'Yüksek', match: 'Exact', note: 'Model bazlı' },
    { keyword: 'ucuz macbook pro', intent: 'Orta', match: 'Phrase', note: 'Fiyat hassas' },
    { keyword: 'refurbished macbook türkiye', intent: 'Yüksek', match: 'Phrase', note: 'İngilizce arayan' },
    { keyword: 'macbook pro fiyat karşılaştırma', intent: 'Araştırma', match: 'Broad', note: 'İçerik hedefli' },
  ];

  return (
    <div>
      <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 mb-6">
        <p className="text-sm"><strong className="text-purple-700">Temel Mesaj:</strong> "Yenilenmiş Mac'te Türkiye'nin tek Apple Yetkili adresi. Profesyoneller için test edilmiş, TSE garantili MacBook. Yenilio'da."</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Rekabet Yoğunluğu', value: 'Az', color: 'text-green-600' },
          { label: 'Ort. Sipariş Değeri', value: 'Yüksek', color: 'text-blue-600' },
          { label: 'Hedef Persona', value: 'Profesyonel', color: 'text-yellow-600' },
          { label: 'Konumlandırma', value: 'Butik', color: 'text-purple-600' },
        ].map((s, i) => (
          <div key={i} className="bg-white rounded-xl border border-gray-200 p-4 text-center shadow-sm">
            <div className={`text-2xl font-bold ${s.color}`}>{s.value}</div>
            <div className="text-xs text-gray-500 mt-1">{s.label}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card title="Google Ads — Anahtar Kelime Planı" subtitle="MacBook'a özel niyet odaklı kelimeler" icon={Search} accent="purple">
          <KeywordTable keywords={keywords} />
        </Card>

        <div className="space-y-6">
          <Card title="Reklam Metni — MacBook Pro" subtitle="'Uzman yenileme merkezi' dili" icon={Monitor} accent="green">
            <AdPreview
              url="yenilio.com/mac/macbook-pro"
              title="Uzman Yenileme Merkezi'nden MacBook Pro — Apple Yetkili, 12 Ay TSE Garantili"
              desc="Profesyoneller için titizlikle test edilmiş MacBook Pro. 27 nokta kontrol, 90 gün değişim hakkı. Türkiye'nin tek Apple yetkili yenilenmiş Mac adresi Yenilio'da."
              extensions={['Apple Yetkili Yenileme', '12 Ay TSE Garanti', '90 Gün Değişim', 'Vade Farksız 12 Taksit']}
            />
          </Card>

          <Card title="Hedef Kitle — MacBook" subtitle="Profesyonel & yaratıcı segment" icon={Users} accent="yellow">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="bg-gray-50"><th className="text-left px-3 py-2 text-xs font-semibold text-gray-600">Katman</th><th className="text-left px-3 py-2 text-xs font-semibold text-gray-600">Kitle</th><th className="text-left px-3 py-2 text-xs font-semibold text-gray-600">Mesaj</th></tr></thead>
                <tbody>
                  <tr className="border-b border-gray-100"><td className="px-3 py-2"><Badge color="blue">Prospecting</Badge></td><td className="px-3 py-2 text-xs">Freelancer, yazılımcı, tasarımcı, fotoğrafçı</td><td className="px-3 py-2 text-xs">"Profesyonel Mac, profesyonel güvence"</td></tr>
                  <tr className="border-b border-gray-100"><td className="px-3 py-2"><Badge color="yellow">Consideration</Badge></td><td className="px-3 py-2 text-xs">Mac sayfa ziyaretçileri + Apple.com ziyaretçileri</td><td className="px-3 py-2 text-xs">"Yeni vs yenilenmiş karşılaştır"</td></tr>
                  <tr><td className="px-3 py-2"><Badge color="green">Conversion</Badge></td><td className="px-3 py-2 text-xs">Mac ATC terk + Trendyol Mac bakanlar</td><td className="px-3 py-2 text-xs">"Aynı kalite, Apple yetkili garanti"</td></tr>
                </tbody>
              </table>
            </div>
          </Card>

          <Card title="Kreatif Yönlendirme" icon={Eye} accent="orange">
            <ul className="text-xs text-gray-600 space-y-1.5">
              <li>• Temiz, minimal arka plan — premium hissi</li>
              <li>• Gerçek (güncel!) MacBook görselleri — <strong className="text-red-500">2016 model KULLANMA</strong></li>
              <li>• Güven rozetleri görsel üzerinde belirgin</li>
              <li>• "Profesyonel kullanım" sahneleri (stüdyo, ofis)</li>
              <li>• Yeni vs Yenilenmiş karşılaştırma görseli</li>
              <li>• Carousel: 27 nokta kontrol süreci görselleri</li>
            </ul>
          </Card>
        </div>
      </div>
    </div>
  );
}

function IPadSection() {
  const segments = [
    { title: 'iPad (Standart/Air)', sub: 'Öğrenci Segmenti', msg: '"Ders notları artık elinizin altında. Yenilenmiş iPad, yeni gibi performans."', tags: ['Öğrenci', 'Eğitimci', '18-30 yaş'], color: 'blue' },
    { title: 'iPad Pro', sub: 'Profesyonel Segmenti', msg: '"Yaratıcılığınızı sınırsız kılın. M çipli iPad Pro, profesyonel güvenceyle."', tags: ['Tasarımcı', 'İçerik Üretici', '25-45 yaş'], color: 'purple' },
    { title: 'iPad Mini', sub: 'Taşınabilirlik Segmenti', msg: '"Her yere sığan güç. Kompakt iPad Mini, Apple yetkili yenileme güvencesiyle."', tags: ['İş insanı', 'Gezgin', '25-50 yaş'], color: 'green' },
  ];

  const keywords = [
    { keyword: 'yenilenmiş ipad', intent: 'Yüksek', match: 'Exact', campaign: 'iPad_Genel' },
    { keyword: 'yenilenmiş ipad pro', intent: 'Yüksek', match: 'Exact', campaign: 'iPad_Pro' },
    { keyword: 'yenilenmiş ipad air', intent: 'Yüksek', match: 'Exact', campaign: 'iPad_Air' },
    { keyword: 'yenilenmiş ipad mini', intent: 'Yüksek', match: 'Exact', campaign: 'iPad_Mini' },
    { keyword: '2. el ipad garantili', intent: 'Yüksek', match: 'Phrase', campaign: 'iPad_Genel' },
    { keyword: 'apple yetkili yenilenmiş ipad', intent: 'Yüksek', match: 'Phrase', campaign: 'iPad_Genel' },
    { keyword: 'öğrenciye ipad', intent: 'Orta', match: 'Broad', campaign: 'iPad_Öğrenci' },
    { keyword: 'ipad eğitim indirimi', intent: 'Orta', match: 'Phrase', campaign: 'iPad_Öğrenci' },
    { keyword: 'ucuz ipad pro', intent: 'Orta', match: 'Phrase', campaign: 'iPad_Pro' },
    { keyword: 'refurbished ipad türkiye', intent: 'Yüksek', match: 'Phrase', campaign: 'iPad_Genel' },
  ];

  return (
    <div>
      <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6">
        <p className="text-sm"><strong className="text-green-700">Temel Mesaj:</strong> "Öğrenciye de profesyonele de: Apple yetkili yenilenmiş iPad. 12 ay TSE garantili, 21 gün iade."</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {segments.map((seg, i) => (
          <div key={i} className={`bg-white rounded-xl border border-gray-200 border-l-4 border-l-${seg.color}-500 p-5 shadow-sm`}>
            <h4 className="font-semibold text-gray-900 text-sm">{seg.title}</h4>
            <p className="text-xs text-gray-500 mb-2">{seg.sub}</p>
            <p className="text-xs text-gray-600 mb-3">{seg.msg}</p>
            <div className="flex flex-wrap gap-1">{seg.tags.map((t, j) => <Badge key={j} color={seg.color}>{t}</Badge>)}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card title="Google Ads — Anahtar Kelime Planı" subtitle="iPad — şu an kampanya HİÇ YOK, sıfırdan kurulacak" icon={Search} accent="green">
          <KeywordTable keywords={keywords} />
        </Card>

        <div className="space-y-6">
          <Card title="Reklam Metni — iPad Öğrenci" icon={Monitor} accent="blue">
            <AdPreview
              url="yenilio.com/ipad"
              title="Öğrenciye Özel Yenilenmiş iPad — Apple Yetkili Yenileme, TSE Garantili"
              desc="Ders notlarından proje sunumlarına. 12 ay TSE garantili, 21 gün iade hakkıyla yenilenmiş iPad Yenilio'da. Gürgençler güvencesiyle eğitime yatırım yap."
              extensions={['12 Ay TSE Garanti', 'Öğrenciye Özel Fırsat', '21 Gün İade', 'Vade Farksız 12 Taksit']}
            />
          </Card>
          <Card title="Reklam Metni — iPad Pro" icon={Monitor} accent="purple">
            <AdPreview
              url="yenilio.com/ipad/ipad-pro"
              title="Yenilenmiş iPad Pro — Uzman Yenileme Merkezi'nden, M Çipli Güç"
              desc="Yaratıcı profesyoneller için. Apple yetkili yenileme, 27 nokta kontrol, 90 gün değişim. M çipli iPad Pro'nun gücünü Yenilio güvencesiyle deneyimle."
              extensions={['Apple Yetkili Yenileme', '90 Gün Değişim', '27 Nokta Kontrol', 'Profesyonel Garanti']}
            />
          </Card>
        </div>
      </div>
    </div>
  );
}

function WatchSection() {
  const segments = [
    { title: 'Watch SE', sub: 'Giriş Segmenti', msg: '"Apple Watch deneyimine en akıllı giriş. Garantili, güvenli, uygun."', tags: ['İlk kez Watch', 'Fitness başlangıç', '18-35 yaş'], color: 'green' },
    { title: 'Watch Series', sub: 'Sağlık & Günlük', msg: '"Sağlığınız bileğinizde. Gelişmiş sensörler, Apple yetkili güvence."', tags: ['Sağlık odaklı', 'Teknoloji meraklısı', '25-50 yaş'], color: 'blue' },
    { title: 'Watch Ultra', sub: 'Macera & Spor', msg: '"Sınırlarınızı zorlayın. Apple Watch Ultra, profesyonel güvenceyle."', tags: ['Outdoor spor', 'Dalış/tırmanış', '25-45 yaş'], color: 'orange' },
  ];

  const keywords = [
    { keyword: 'yenilenmiş apple watch', intent: 'Yüksek', match: 'Exact' },
    { keyword: 'yenilenmiş apple watch ultra', intent: 'Yüksek', match: 'Exact' },
    { keyword: 'yenilenmiş apple watch se', intent: 'Yüksek', match: 'Exact' },
    { keyword: '2. el apple watch garantili', intent: 'Yüksek', match: 'Phrase' },
    { keyword: 'apple watch tse garantili', intent: 'Yüksek', match: 'Phrase' },
    { keyword: 'ikinci el akıllı saat apple', intent: 'Orta', match: 'Phrase' },
    { keyword: 'ucuz apple watch', intent: 'Orta', match: 'Broad' },
    { keyword: 'refurbished apple watch türkiye', intent: 'Yüksek', match: 'Phrase' },
  ];

  return (
    <div>
      <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 mb-6">
        <p className="text-sm"><strong className="text-orange-700">Temel Mesaj:</strong> "Bileğinize güvenle takın. Apple Yetkili Yenileme Merkezi'nden yenilenmiş Apple Watch. 12 ay TSE garantili, 90 gün değişim hakkıyla."</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {segments.map((seg, i) => (
          <div key={i} className={`bg-white rounded-xl border border-gray-200 border-l-4 border-l-${seg.color}-500 p-5 shadow-sm`}>
            <h4 className="font-semibold text-gray-900 text-sm">{seg.title}</h4>
            <p className="text-xs text-gray-500 mb-2">{seg.sub}</p>
            <p className="text-xs text-gray-600 mb-3">{seg.msg}</p>
            <div className="flex flex-wrap gap-1">{seg.tags.map((t, j) => <Badge key={j} color={seg.color}>{t}</Badge>)}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card title="Google Ads — Anahtar Kelime Planı" subtitle="Watch — şu an kampanya YOK, sıfırdan kurulacak" icon={Search} accent="orange">
          <KeywordTable keywords={keywords} />
        </Card>

        <div className="space-y-6">
          <Card title="Reklam Metni — Watch Series" icon={Monitor} accent="blue">
            <AdPreview
              url="yenilio.com/apple-watch"
              title="Apple Yetkili Yenilenmiş Watch — 12 Ay TSE Garantili, Güvenle Takın"
              desc="Sağlık takibi, fitness ve günlük kullanım için. 27 nokta kontrollü, 21 gün iade, 90 gün değişim hakkıyla Apple Watch. Yenilio'da."
              extensions={['Apple Yetkili', '12 Ay TSE Garanti', '90 Gün Değişim', 'Ücretsiz Kargo']}
            />
          </Card>
          <Card title="Hedef Kitle — Meta" icon={Users} accent="yellow">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="bg-gray-50"><th className="text-left px-3 py-2 text-xs font-semibold text-gray-600">Segment</th><th className="text-left px-3 py-2 text-xs font-semibold text-gray-600">İlgi Alanları</th><th className="text-left px-3 py-2 text-xs font-semibold text-gray-600">Yaş</th></tr></thead>
                <tbody>
                  <tr className="border-b border-gray-100"><td className="px-3 py-2 text-xs">Fitness</td><td className="px-3 py-2 text-xs">Koşu, spor salonu, sağlıklı yaşam</td><td className="px-3 py-2 text-xs">22-40</td></tr>
                  <tr className="border-b border-gray-100"><td className="px-3 py-2 text-xs">Sağlık</td><td className="px-3 py-2 text-xs">Kalp ritmi, uyku izleme</td><td className="px-3 py-2 text-xs">30-55</td></tr>
                  <tr className="border-b border-gray-100"><td className="px-3 py-2 text-xs">Teknoloji</td><td className="px-3 py-2 text-xs">Apple Watch, wearable tech</td><td className="px-3 py-2 text-xs">20-45</td></tr>
                  <tr><td className="px-3 py-2 text-xs">Macera (Ultra)</td><td className="px-3 py-2 text-xs">Outdoor spor, dalış, maraton</td><td className="px-3 py-2 text-xs">25-45</td></tr>
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}

function GoogleAdsSection() {
  const campaigns = [
    { name: 'SRCH_BRND_Yenilio', type: 'Search - Brand', target: 'Brand koruma', isNew: false },
    { name: 'SRCH_GNRC_iPhone_13', type: 'Search', target: 'iPhone 13', isNew: false },
    { name: 'SRCH_GNRC_iPhone_14', type: 'Search', target: 'iPhone 14', isNew: false },
    { name: 'SRCH_GNRC_iPhone_15', type: 'Search', target: 'iPhone 15', isNew: false },
    { name: 'SRCH_GNRC_MacBook ★', type: 'Search', target: 'MacBook (YENİ)', isNew: true },
    { name: 'SRCH_GNRC_iPad ★', type: 'Search', target: 'iPad (YENİ)', isNew: true },
    { name: 'SRCH_GNRC_Watch ★', type: 'Search', target: 'Apple Watch (YENİ)', isNew: true },
    { name: 'SRCH_COMP', type: 'Search - Rakip', target: 'getmobil, easycep vb.', isNew: false },
    { name: 'SRCH_GNRC_Trust ★', type: 'Search', target: '"yenilenmiş güvenilir mi" (YENİ)', isNew: true },
    { name: 'PMAX_Yenilio', type: 'Performance Max', target: 'Dinamik katalog', isNew: false },
  ];

  const newKeywords = ['yenilenmiş apple ürünleri', 'TSE garantili telefon', 'apple yetkili yenileme', 'güvenilir yenilenmiş iphone', 'garantili 2. el apple', 'resmi yenilenmiş mac', 'yenilenmiş apple güvenilir mi', 'ticaret bakanlığı onaylı yenilenmiş', '27 nokta kontrollü iphone', 'refurbished apple türkiye'];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card title="Kampanya Yapısı (Revize)" icon={Layers} accent="blue">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="bg-gray-50"><th className="text-left px-3 py-2 text-xs font-semibold text-gray-600">Kampanya</th><th className="text-left px-3 py-2 text-xs font-semibold text-gray-600">Tip</th><th className="text-left px-3 py-2 text-xs font-semibold text-gray-600">Hedef</th></tr></thead>
            <tbody>
              {campaigns.map((c, i) => (
                <tr key={i} className={`border-b border-gray-100 ${c.isNew ? 'bg-green-50' : ''}`}>
                  <td className={`px-3 py-2 text-xs font-mono ${c.isNew ? 'text-green-700 font-semibold' : ''}`}>{c.name}</td>
                  <td className="px-3 py-2 text-xs text-gray-600">{c.type}</td>
                  <td className="px-3 py-2 text-xs text-gray-600">{c.target}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <div className="space-y-6">
        <Card title="Brand vs Non-Brand Raporlama" icon={BarChart3} accent="yellow">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="bg-gray-50"><th className="text-left px-3 py-2 text-xs font-semibold text-gray-600">Metrik</th><th className="px-3 py-2 text-xs font-semibold text-gray-600">Brand</th><th className="px-3 py-2 text-xs font-semibold text-gray-600">Non-Brand</th><th className="px-3 py-2 text-xs font-semibold text-gray-600">Rakip</th></tr></thead>
              <tbody>
                <tr className="border-b border-gray-100"><td className="px-3 py-2 text-xs">ROAS Hedef</td><td className="px-3 py-2 text-xs text-center">30x+</td><td className="px-3 py-2 text-xs text-center">10x+</td><td className="px-3 py-2 text-xs text-center">5x+</td></tr>
                <tr className="border-b border-gray-100"><td className="px-3 py-2 text-xs">CPA</td><td className="px-3 py-2 text-xs text-center">Düşük</td><td className="px-3 py-2 text-xs text-center">Orta</td><td className="px-3 py-2 text-xs text-center">Yüksek</td></tr>
                <tr><td className="px-3 py-2 text-xs">Conv. Rate</td><td className="px-3 py-2 text-xs text-center">%2+</td><td className="px-3 py-2 text-xs text-center">%0.5+</td><td className="px-3 py-2 text-xs text-center">%0.3+</td></tr>
              </tbody>
            </table>
          </div>
        </Card>

        <Card title="Niyet Odaklı Yeni Kelimeler (Eklenmeli)" icon={Search} accent="green">
          <div className="flex flex-wrap gap-2">
            {newKeywords.map((kw, i) => <Badge key={i} color="green">{kw}</Badge>)}
          </div>
        </Card>

        <Card title="Extension Güncellemesi" icon={Layers} accent="purple">
          <div className="grid grid-cols-2 gap-3">
            {[
              { title: 'Sitelink\'ler', items: ['iPhone Modelleri', 'MacBook Modelleri', 'iPad Modelleri', 'Watch Modelleri', 'Neden Yenilio?', 'Garanti & İade'] },
              { title: 'Callout\'lar', items: ['Apple Yetkili Yenileme', '12 Ay TSE Garanti', '21 Gün İade Hakkı', '90 Gün Değişim', '27 Nokta Kontrol', 'Ücretsiz Kargo'] },
            ].map((ext, i) => (
              <div key={i} className="bg-gray-50 rounded-lg p-3">
                <div className="font-semibold text-xs text-gray-700 mb-2">{ext.title}</div>
                <ul className="text-xs text-gray-500 space-y-1">{ext.items.map((item, j) => <li key={j}>• {item}</li>)}</ul>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}

function MetaAdsSection() {
  const campaigns = [
    { name: 'YNL_PROSP_iPhone', goal: 'Prospecting', audience: 'Soğuk - Apple & iPhone ilgililer', budget: '%25', kpi: 'CPM, CTR' },
    { name: 'YNL_PROSP_Mac', goal: 'Prospecting', audience: 'Soğuk - Profesyonel/yaratıcı', budget: '%10', kpi: 'CPM, CTR' },
    { name: 'YNL_PROSP_iPad', goal: 'Prospecting', audience: 'Soğuk - Öğrenci/eğitimci', budget: '%10', kpi: 'CPM, CTR' },
    { name: 'YNL_PROSP_Watch', goal: 'Prospecting', audience: 'Soğuk - Fitness/sağlık', budget: '%5', kpi: 'CPM, CTR' },
    { name: 'YNL_CONSIDER', goal: 'Consideration', audience: 'Site ziyaretçileri 7-30 gün', budget: '%20', kpi: 'CTR, ATC' },
    { name: 'YNL_RETARGET', goal: 'Conversion', audience: 'ATC + Checkout terk 0-14 gün', budget: '%20', kpi: 'ROAS, CPA' },
    { name: 'YNL_DPA_CATALOG', goal: 'Conversion', audience: 'Dinamik ürün kataloğu', budget: '%10', kpi: 'ROAS' },
  ];

  const goalColors = { Prospecting: 'blue', Consideration: 'yellow', Conversion: 'green' };

  return (
    <div className="space-y-6">
      <Card title="Kampanya Mimarisi (Revize)" icon={Megaphone} accent="purple">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="bg-gray-50"><th className="text-left px-3 py-2 text-xs font-semibold text-gray-600">Kampanya</th><th className="text-left px-3 py-2 text-xs font-semibold text-gray-600">Hedef</th><th className="text-left px-3 py-2 text-xs font-semibold text-gray-600">Kitle</th><th className="text-left px-3 py-2 text-xs font-semibold text-gray-600">Bütçe</th><th className="text-left px-3 py-2 text-xs font-semibold text-gray-600">KPI</th></tr></thead>
            <tbody>
              {campaigns.map((c, i) => (
                <tr key={i} className="border-b border-gray-100 hover:bg-gray-50"><td className="px-3 py-2 text-xs font-mono">{c.name}</td><td className="px-3 py-2"><Badge color={goalColors[c.goal]}>{c.goal}</Badge></td><td className="px-3 py-2 text-xs text-gray-600">{c.audience}</td><td className="px-3 py-2 text-xs font-semibold">{c.budget}</td><td className="px-3 py-2 text-xs text-gray-500">{c.kpi}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card title="Kreatif Plan — Ürün Grubuna Özel" icon={Eye} accent="blue">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="bg-gray-50"><th className="text-left px-3 py-2 text-xs font-semibold text-gray-600">Ürün</th><th className="text-left px-3 py-2 text-xs font-semibold text-gray-600">Kreatif</th><th className="text-left px-3 py-2 text-xs font-semibold text-gray-600">Ana Mesaj</th></tr></thead>
              <tbody>
                <tr className="border-b border-gray-100"><td className="px-3 py-2 text-xs font-medium">iPhone</td><td className="px-3 py-2 text-xs">Statik + Video</td><td className="px-3 py-2 text-xs">"Eskiyi ver, yenisine güvenle geç"</td></tr>
                <tr className="border-b border-gray-100"><td className="px-3 py-2 text-xs font-medium">MacBook</td><td className="px-3 py-2 text-xs">Lifestyle + Karşılaştırma</td><td className="px-3 py-2 text-xs">"Profesyonel Mac, profesyonel güvence"</td></tr>
                <tr className="border-b border-gray-100"><td className="px-3 py-2 text-xs font-medium">iPad</td><td className="px-3 py-2 text-xs">Kullanım senaryosu</td><td className="px-3 py-2 text-xs">"Eğitimden yaratıcılığa, garantili iPad"</td></tr>
                <tr><td className="px-3 py-2 text-xs font-medium">Watch</td><td className="px-3 py-2 text-xs">Sağlık/fitness</td><td className="px-3 py-2 text-xs">"Bileğinize güvenle takın"</td></tr>
              </tbody>
            </table>
          </div>
        </Card>

        <Card title="Kitle Genişletme — Mevcut vs Eklenecek" icon={Users} accent="green">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="bg-gray-50"><th className="text-left px-3 py-2 text-xs font-semibold text-gray-600">Mevcut</th><th className="text-left px-3 py-2 text-xs font-semibold text-gray-600">Eklenecek</th></tr></thead>
              <tbody>
                <tr className="border-b border-gray-100"><td className="px-3 py-2 text-xs">catalog_purchase_90days (~1000)</td><td className="px-3 py-2 text-xs text-green-600">Purchase lookalike %1, %3, %5</td></tr>
                <tr className="border-b border-gray-100"><td className="px-3 py-2 text-xs">AllVisit_180</td><td className="px-3 py-2 text-xs text-green-600">Sayfa bazlı: Mac, iPad ziyaretçileri</td></tr>
                <tr className="border-b border-gray-100"><td className="px-3 py-2 text-xs">Apple + iPhone interest</td><td className="px-3 py-2 text-xs text-green-600">Watch + Wearable interest ayrı</td></tr>
                <tr className="border-b border-gray-100"><td className="px-3 py-2 text-xs">—</td><td className="px-3 py-2 text-xs text-green-600">Trendyol Yenilio alıcı lookalike</td></tr>
                <tr><td className="px-3 py-2 text-xs">—</td><td className="px-3 py-2 text-xs text-green-600">Video izleyici audience (75%+)</td></tr>
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </div>
  );
}

function FunnelSection() {
  const hypotheses = [
    { h: 'Platform güveni eksik (Trendyol\'da marka bilinirliği)', p: 'Yüksek', test: 'Güven elementleri A/B testi' },
    { h: 'Landing page mesaj hiyerarşisi yanlış', p: 'Yüksek', test: 'Heatmap + session recording' },
    { h: 'Remarketing eksikliği (catalog audience ~1000)', p: 'Yüksek', test: 'Retargeting genişletme' },
    { h: 'Checkout sürecinde friction', p: 'Orta', test: 'Funnel analizi (GA4)' },
    { h: 'Mobil UX sorunları', p: 'Orta', test: 'Device kırılımlı analiz' },
    { h: 'Fiyat algısı (Trendyol kupon avantajı)', p: 'Orta', test: 'Fiyat parite analizi' },
  ];

  const actions = [
    { a: 'Landing page güven hiyerarşisini above-the-fold\'a taşı', impact: 'Yüksek', time: '1 hafta' },
    { a: 'Ürün sayfalarına TSE + Apple Yetkili badge ekle', impact: 'Yüksek', time: '1 hafta' },
    { a: '"27 Nokta Kontrol Süreci" bilgi bölümü ekle', impact: 'Yüksek', time: '2 hafta' },
    { a: 'Checkout\'ta güven rozetleri', impact: 'Orta', time: '1 hafta' },
    { a: 'Müşteri yorumları / sosyal kanıt bölümü', impact: 'Orta', time: '2 hafta' },
    { a: '"Neden Yenilio?" karşılaştırma sayfası', impact: 'Orta', time: '2 hafta' },
    { a: 'Exit-intent popup (güven mesajlı)', impact: 'Orta', time: '3 gün' },
    { a: 'Hotjar/Clarity ile session recording başlat', impact: 'Yüksek', time: '1 gün' },
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card title="Yenilio.com Neden Dönüştürmüyor?" subtitle="Hipotezler" icon={XCircle} accent="red">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="bg-gray-50"><th className="text-left px-3 py-2 text-xs font-semibold text-gray-600">Hipotez</th><th className="px-3 py-2 text-xs font-semibold text-gray-600">Olasılık</th><th className="text-left px-3 py-2 text-xs font-semibold text-gray-600">Test</th></tr></thead>
              <tbody>
                {hypotheses.map((h, i) => (
                  <tr key={i} className="border-b border-gray-100"><td className="px-3 py-2 text-xs">{h.h}</td><td className="px-3 py-2 text-center"><Badge color={h.p === 'Yüksek' ? 'red' : 'yellow'}>{h.p}</Badge></td><td className="px-3 py-2 text-xs text-gray-500">{h.test}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        <Card title="Yenilio.com CRO Aksiyon Planı" icon={CheckCircle2} accent="green">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="bg-gray-50"><th className="text-left px-3 py-2 text-xs font-semibold text-gray-600">Aksiyon</th><th className="px-3 py-2 text-xs font-semibold text-gray-600">Etki</th><th className="px-3 py-2 text-xs font-semibold text-gray-600">Süre</th></tr></thead>
              <tbody>
                {actions.map((a, i) => (
                  <tr key={i} className="border-b border-gray-100"><td className="px-3 py-2 text-xs">{a.a}</td><td className="px-3 py-2 text-center"><Badge color={a.impact === 'Yüksek' ? 'green' : 'yellow'}>{a.impact}</Badge></td><td className="px-3 py-2 text-xs text-gray-500 text-center">{a.time}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      <Card title="Funnel Kırılımı — Takip Edilecek Metrikler" icon={TrendingUp} accent="blue">
        <div className="space-y-3">
          {[
            { stage: 'TOFU — Awareness', color: 'blue', metrics: 'Impression, Reach, CPM, Video View Rate', target: 'Marka bilinirliği + güvenilir yenilenmiş algısı', channel: 'Meta Prospecting + Google Display + YouTube' },
            { stage: 'MOFU — Consideration', color: 'yellow', metrics: 'CTR, Site Ziyareti, Sayfa/Oturum, Ort. Süre', target: 'CTR > %2, Sayfa/Oturum > 3, Bounce < %50', channel: 'Google Search (niyet) + Meta Consideration + Remarketing' },
            { stage: 'BOFU — Conversion', color: 'green', metrics: 'ATC Rate, Checkout Rate, Purchase Rate, ROAS, CPA', target: 'ATC > %3, Purchase > %0.5, ROAS > 15x', channel: 'Meta Retargeting + Google Brand + DPA Catalog' },
          ].map((f, i) => (
            <div key={i} className={`bg-${f.color}-50 border border-${f.color}-200 rounded-lg p-4`}>
              <div className={`font-semibold text-${f.color}-700 text-sm mb-1`}>{f.stage}</div>
              <div className="text-xs text-gray-600 space-y-0.5">
                <div><strong>Metrikler:</strong> {f.metrics}</div>
                <div><strong>Hedef:</strong> {f.target}</div>
                <div><strong>Kanal:</strong> {f.channel}</div>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

function ActionsSection() {
  const weeks = [
    { week: 'Hafta 1 (Mart 6-12)', title: 'ACİL — Yapısal Değişiklikler', color: 'red', actions: [
      { action: 'Tüm Yenilio RSA\'larda H1-H2 pin\'leme (güven başlıkları)', platform: 'Google', owner: 'SEM' },
      { action: 'Callout güncelle: 21 gün iade + 90 gün değişim', platform: 'Google', owner: 'SEM' },
      { action: 'MacBook, iPad, Watch için ayrı kampanya oluştur', platform: 'Google', owner: 'SEM' },
      { action: 'Ürün grubu bazlı prospecting kampanyaları kur', platform: 'Meta', owner: 'Social' },
      { action: 'Retargeting audience genişlet (lookalike %1-%3)', platform: 'Meta', owner: 'Social' },
      { action: 'Hotjar/Clarity kurulumu', platform: 'Site', owner: 'Dev' },
    ]},
    { week: 'Hafta 2 (Mart 13-19)', title: 'KREATİF — Yeni İçerik Üretimi', color: 'yellow', actions: [
      { action: 'MacBook premium kreatifler (3 statik + 1 video)', platform: 'Meta', owner: 'Tasarım' },
      { action: 'iPad öğrenci kreatifleri (3 statik + 1 carousel)', platform: 'Meta', owner: 'Tasarım' },
      { action: 'Watch fitness/sağlık kreatifleri (3 statik + 1 video)', platform: 'Meta', owner: 'Tasarım' },
      { action: 'Güven rozeti standart şablonu oluştur', platform: 'Tümü', owner: 'Tasarım' },
      { action: '"Neden Yenilio?" karşılaştırma görseli', platform: 'Meta + Site', owner: 'Tasarım' },
      { action: '27 Nokta Kontrol infographic', platform: 'Meta + Site', owner: 'Tasarım' },
    ]},
    { week: 'Hafta 3 (Mart 20-26)', title: 'OPTİMİZASYON — Site & Funnel', color: 'green', actions: [
      { action: 'Landing page güven elementleri above-the-fold\'a taşı', platform: 'Site', owner: 'Dev + UX' },
      { action: 'Ürün sayfalarına TSE + Apple Yetkili badge', platform: 'Site', owner: 'Dev' },
      { action: 'Checkout güven rozetleri ekle', platform: 'Site', owner: 'Dev' },
      { action: 'Session recording analizi', platform: 'Analytics', owner: 'UX' },
      { action: 'A/B test başlat: güven vs fiyat landing page', platform: 'Site', owner: 'Growth' },
      { action: 'Yeni kampanyaların ilk performans raporu', platform: 'Google + Meta', owner: 'SEM' },
    ]},
    { week: 'Hafta 4 (Mart 27 - Nisan 2)', title: 'RAPORLAMA — Sonuç & İterasyon', color: 'purple', actions: [
      { action: 'Brand vs Non-brand performans raporu', platform: 'Google', owner: 'SEM' },
      { action: 'Ürün grubu bazlı ROAS raporu', platform: 'Google + Meta', owner: 'Analytics' },
      { action: 'Funnel kırılımı: CTR → ATC → Purchase', platform: 'GA4', owner: 'Analytics' },
      { action: 'Trendyol vs Yenilio.com karşılaştırma raporu', platform: 'Analytics', owner: 'Analytics' },
      { action: 'A/B test sonuçları ve kararlar', platform: 'Site', owner: 'Growth' },
      { action: 'Ali Bey\'e kapsamlı revize sunum', platform: 'Sunum', owner: 'Tüm ekip' },
    ]},
  ];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {weeks.map((w, i) => (
        <Card key={i} title={w.week} subtitle={w.title} accent={w.color}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="bg-gray-50"><th className="text-left px-3 py-2 text-xs font-semibold text-gray-600">Aksiyon</th><th className="px-3 py-2 text-xs font-semibold text-gray-600">Platform</th><th className="px-3 py-2 text-xs font-semibold text-gray-600">Sorumlu</th></tr></thead>
              <tbody>
                {w.actions.map((a, j) => (
                  <tr key={j} className="border-b border-gray-100"><td className="px-3 py-2 text-xs">{a.action}</td><td className="px-3 py-2 text-xs text-gray-500 text-center">{a.platform}</td><td className="px-3 py-2 text-xs text-center"><Badge color="gray">{a.owner}</Badge></td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      ))}
    </div>
  );
}

// ─── MAIN PAGE ───────────────────────────────────────────────

const sectionComponents = {
  trust: TrustSection,
  iphone: IPhoneSection,
  macbook: MacBookSection,
  ipad: IPadSection,
  watch: WatchSection,
  google: GoogleAdsSection,
  meta: MetaAdsSection,
  funnel: FunnelSection,
  actions: ActionsSection,
};

const sectionTitles = {
  trust: 'Marka Mesaj Mimarisi & Güven Hiyerarşisi',
  iphone: 'iPhone Stratejisi — Hacim Ürünü, Güven Odaklı',
  macbook: 'MacBook Stratejisi — Premium, Uzman Yenileme',
  ipad: 'iPad Stratejisi — Öğrenci + Profesyonel',
  watch: 'Apple Watch Stratejisi — Garantili Güvenli Alternatif',
  google: 'Google Ads — Genel Yapı Planı',
  meta: 'Meta Ads — Kampanya Yapısı',
  funnel: 'Funnel Yapısı & Yenilio.com CRO Planı',
  actions: '30 Günlük Revize Aksiyon Planı',
};

export default function YenilioStrategyPlan() {
  const [activeTab, setActiveTab] = useState('trust');
  const SectionComponent = sectionComponents[activeTab];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero */}
      <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white py-16 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl font-bold mb-3 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
            Yenilio Dijital Strateji Planı
          </h1>
          <p className="text-gray-400 text-lg mb-4">Perşembe Toplantısı Hazırlık Dokümanı — Bilişim Vadisi</p>
          <span className="inline-block bg-gray-800 border border-gray-700 px-5 py-2 rounded-full text-sm text-blue-300 font-medium">
            Mart 2026 • Revize Aksiyon Planı
          </span>
        </div>
      </div>

      {/* Tabs */}
      <div className="sticky top-0 z-50 bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 overflow-x-auto">
          <div className="flex gap-0 min-w-max">
            {tabs.map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-3.5 text-sm font-medium whitespace-nowrap border-b-2 transition-all ${
                    activeTab === tab.id
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">{sectionTitles[activeTab]}</h2>
        <SectionComponent />
      </div>
    </div>
  );
}
